/**
 * The voice-note transport's decisions (W8) — pure functions, so `bun test` pins the speed cycle and
 * the seek arithmetic instead of the component re-deriving them. `soundRules.ts` keeps the same
 * shape for the same reason: a rule that lives in one callable place can be read, argued with and
 * tested, and a rule that lives inside a pointer handler cannot.
 */
import { isolateLtr } from './bidi'

/**
 * The speeds a voice note cycles through, in order. Three, because a recording of a family member
 * is either at the speed they spoke it or one of the two speeds at which it is still the same
 * sentence; and in one direction, because a control that a thumb can press again is a control that
 * needs no menu.
 */
export const SPEEDS = [1, 1.5, 2] as const

export type VoiceSpeed = (typeof SPEEDS)[number]

/** How far one ArrowLeft/ArrowRight press moves the position, in seconds. */
export const SEEK_STEP_SECONDS = 5

/**
 * The next speed in the cycle: 1 → 1.5 → 2 → 1. A value that is not in the cycle — no caller here
 * produces one — restarts at 1×, so the control is never left pointing at no state at all.
 */
export function nextSpeed(current: number): VoiceSpeed {
  const index = SPEEDS.indexOf(current as VoiceSpeed)
  return SPEEDS[(index + 1) % SPEEDS.length] as VoiceSpeed
}

/**
 * `1.5×` — the chip's text, and the speed named inside the control's label. Isolated: a decimal is
 * two number runs around a point, so the whole value is one object wherever it lands
 * (docs/DESIGN.md §7.9), and this one lands inside a sentence in both chromes.
 */
export function speedLabel(speed: number): string {
  return isolateLtr(`${speed}×`)
}

/**
 * Where a pointer is across a track: its share of the width, clamped to `[0, 1]`. A track that has
 * not been laid out yet has no share to give, so the start is the honest answer.
 */
export function fractionFromPosition(clientX: number, rect: { left: number; width: number }): number {
  if (!(rect.width > 0)) return 0
  return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
}

/**
 * The position a fraction of the note means, in seconds — the unit `audio.currentTime` wants.
 * Clamped to the note: a drag past either end is that end, never a position outside it.
 */
export function secondsFromFraction(fraction: number, durationMs: number): number {
  if (!(durationMs > 0)) return 0
  return (Math.min(1, Math.max(0, fraction)) * durationMs) / 1000
}

/** A keyboard step from where the note is now. The ends are the ends, never past them. */
export function steppedSeconds(currentSeconds: number, deltaSeconds: number, durationMs: number): number {
  if (!(durationMs > 0)) return 0
  return Math.min(durationMs / 1000, Math.max(0, currentSeconds + deltaSeconds))
}
